/* Gestão de releases do app (ADR-0659). Lista as versões (índice), registra a partir do que subiu no
   R2, PROMOVE (disponibiliza para os usuários), marca OBRIGATÓRIA e TIRA DO AR. super_admin.
   Layout de lista padronizado (ADR-0702/0704): busca por versão + filtros (canal/status/plataforma/
   obrigatória) agora SERVER-SIDE — a aba "Releases" é paginada por cursor keyset (só a página atual
   chega, então filtrar client-side não funciona; espelha empresas/usuários/auditoria). "Carregar mais"
   busca a próxima página. A visão bruta do bucket R2 fica numa aba separada (lazy, NÃO paginada) — a
   query do bucket só dispara quando o staff abre a auditoria de storage.
   ADR-0809 acrescenta métrica e lote; ADR-0825 faz DELETE remover somente índice/métricas,
   preservando os objetos canônicos no R2/GitHub e recusando a versão que ainda está no ar. */
import type {
  ReleaseBulkAction,
  ReleaseChannel,
  ReleasePlatform,
  ReleaseStatus,
  ReleaseWithMetrics,
  StaffRole,
} from '@planuze/admin-contracts';
import {
  RELEASE_BULK_MAX_IDS,
  RELEASE_CHANNELS,
  RELEASE_PLATFORMS,
  RELEASE_STATUSES,
} from '@planuze/admin-contracts';
import {
  Button,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { FacetMultiSelect } from '../components/filters/facet-multi-select.js';
import { SegmentedControl, type SegmentOption } from '../components/filters/segmented-control.js';
import { ReleaseBucketPanel } from '../components/releases/release-bucket-panel.js';
import { ReleaseBulkBar } from '../components/releases/release-bulk-bar.js';
import {
  ReleaseConfirmDialog,
  type ReleasePending,
  type ReleasePurgeConfirmation,
} from '../components/releases/release-confirm-dialog.js';
import { ReleaseNotesModal } from '../components/releases/release-notes-modal.js';
import {
  releaseErrorMessage,
  releaseItemErrorMessage,
} from '../components/releases/releases-error.js';
import { ReleasesTable } from '../components/releases/releases-table.js';
import { SearchInput } from '../components/search-input.js';
import { useReleasesContent } from '../content/sections/releases.js';
import { useDebounce } from '../hooks/use-debounce.js';
import {
  useBulkReleaseAction,
  useDeleteRelease,
  usePromoteRelease,
  usePurgeReleaseVersion,
  useReleasesList,
  useTakedownRelease,
  useUpdateReleaseMandatory,
} from '../hooks/use-releases.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin'];

/* Rótulos de plataforma: nomes próprios (macOS/Windows/Linux), idênticos nos dois idiomas — não
   entram na copy bilíngue. O rótulo de status vem de `ReleasesContent.statusLabels`. */
const PLATFORM_LABELS: Record<ReleasePlatform, string> = {
  mac: 'macOS',
  win: 'Windows',
  linux: 'Linux',
};

type ReleaseTab = 'releases' | 'bucket';
type ChannelFilter = 'all' | ReleaseChannel;
type StatusFilter = 'all' | ReleaseStatus;
type MandatoryFilter = 'all' | 'mandatory' | 'optional';

export function ReleasesPage() {
  const copy = useReleasesContent();
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const promote = usePromoteRelease();
  const takedown = useTakedownRelease();
  const remove = useDeleteRelease();
  const purge = usePurgeReleaseVersion();
  const bulk = useBulkReleaseAction();
  const updateMandatory = useUpdateReleaseMandatory();
  const navigate = useNavigate();
  const [notesFor, setNotesFor] = useState<ReleaseWithMetrics | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<ReleasePending | null>(null);
  const [activeTab, setActiveTab] = useState<ReleaseTab>('releases');
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [mandatoryFilter, setMandatoryFilter] = useState<MandatoryFilter>('all');
  const [platforms, setPlatforms] = useState<string[]>([]);

  const debouncedSearch = useDebounce(search, 300);
  const hasSearch = debouncedSearch.trim().length > 0;
  /* Filtros server-side (a lista é paginada). O objeto compõe a queryKey do infinite-query;
     TanStack faz hash por valor, então recriar a cada render não re-dispara sem mudança real. */
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useReleasesList({
      channel: channelFilter === 'all' ? undefined : channelFilter,
      status: statusFilter === 'all' ? undefined : statusFilter,
      mandatory: mandatoryFilter === 'all' ? undefined : mandatoryFilter === 'mandatory',
      platforms: platforms.length > 0 ? (platforms as ReleasePlatform[]) : undefined,
      search: hasSearch ? debouncedSearch.trim() : undefined,
    });
  const releases = data?.pages.flatMap((page) => page.items) ?? [];

  const onPromote = (release: ReleaseWithMetrics, mandatory: boolean): void => {
    promote.mutate(
      { id: release.id, mandatory },
      {
        onSuccess: () =>
          toast.success(mandatory ? copy.toastPromotedMandatory : copy.toastPromoted),
        onError: (err) => toast.error(releaseErrorMessage(err)),
      },
    );
  };
  const onTakedown = (release: ReleaseWithMetrics): void => {
    takedown.mutate(release.id, {
      onSuccess: () => toast.success(copy.toastTakenDown),
      onError: (err) => toast.error(releaseErrorMessage(err)),
    });
  };

  const busy =
    promote.isPending ||
    takedown.isPending ||
    remove.isPending ||
    purge.isPending ||
    bulk.isPending ||
    updateMandatory.isPending;

  const toggle = (id: string): void =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < RELEASE_BULK_MAX_IDS) next.add(id);
      return next;
    });
  /* Marca só o que está na página carregada — a lista é paginada por cursor e "todas" abrangeria
     versões que nunca chegaram ao cliente. */
  const toggleAll = (checked: boolean): void =>
    setSelectedIds(
      checked
        ? new Set(releases.slice(0, RELEASE_BULK_MAX_IDS).map((release) => release.id))
        : new Set(),
    );

  /* A seleção EFETIVA é a interseção com o que está carregado, não o Set cru. Trocar um filtro ou
     a busca troca a lista sob a seleção, e um lote destrutivo aplicado a ids que saíram da tela
     apagaria coisa que ninguém está vendo. Filtrar aqui faz a contagem do Dialog e o que é enviado
     serem sempre a mesma coisa. */
  const selectedVisible = releases.filter((r) => selectedIds.has(r.id));

  /* Nenhuma ação destrutiva (nem em lote) sai daqui sem passar pelo Dialog. */
  const requestBulk = (action: ReleaseBulkAction): void =>
    setPending({
      kind: 'bulk',
      ids: selectedVisible.slice(0, RELEASE_BULK_MAX_IDS).map((release) => release.id),
      action,
    });

  const runPending = (purgeConfirmation?: ReleasePurgeConfirmation): void => {
    if (pending === null) return;
    if (pending.kind === 'delete') {
      remove.mutate(pending.release.id, {
        onSuccess: (result) => {
          toast.success(
            result.objectDeleted ? copy.toastDeletedFromR2 : copy.toastRecordOnlyRemoved,
          );
          setPending(null);
          setSelectedIds(new Set());
        },
        onError: (err) => toast.error(releaseErrorMessage(err)),
      });
      return;
    }
    if (pending.kind === 'purge') {
      if (purgeConfirmation === undefined) return;
      purge.mutate(
        {
          channel: pending.release.channel,
          version: pending.release.version,
          confirmation: purgeConfirmation.confirmation,
          reason: purgeConfirmation.reason,
        },
        {
          onSuccess: (result) => {
            toast.success(
              copy.toastVersionPurgedFromR2(result.objectsDeleted, result.bytesDeleted),
            );
            setPending(null);
            setSelectedIds(new Set());
          },
          onError: (err) => toast.error(releaseErrorMessage(err)),
        },
      );
      return;
    }
    bulk.mutate(
      { ids: pending.ids, action: pending.action },
      {
        onSuccess: (res) => {
          const failed = res.results.filter((r) => !r.ok);
          if (failed.length === 0) {
            toast.success(copy.toastBulkApplied(res.results.length));
          } else {
            /* O primeiro código de falha vira texto: o lote é parcialmente aplicável e "N com
               falha" sem o motivo obrigaria o staff a repetir item a item para descobri-lo. */
            toast.warning(
              copy.toastBulkPartial(
                res.results.length - failed.length,
                failed.length,
                releaseItemErrorMessage(failed[0]?.error ?? null),
              ),
            );
          }
          setPending(null);
          setSelectedIds(new Set());
        },
        onError: (err) => toast.error(releaseErrorMessage(err)),
      },
    );
  };

  /* Contadores por status foram removidos: com paginação server-side, só a página atual chega ao
     cliente — um count derivado dela seria enganoso (exigiria endpoint de agregação à parte). */
  const channelOptions: readonly SegmentOption<ChannelFilter>[] = [
    { value: 'all', label: copy.allLabel },
    ...RELEASE_CHANNELS.map((c) => ({ value: c, label: c })),
  ];
  const statusOptions: readonly SegmentOption<StatusFilter>[] = [
    { value: 'all', label: copy.allLabel },
    ...RELEASE_STATUSES.map((s) => ({ value: s, label: copy.statusLabels[s] })),
  ];
  const mandatoryOptions: readonly SegmentOption<MandatoryFilter>[] = [
    { value: 'all', label: copy.mandatoryAllLabel },
    { value: 'mandatory', label: copy.mandatoryOnlyLabel },
    { value: 'optional', label: copy.optionalLabel },
  ];

  const hasFilters =
    hasSearch ||
    channelFilter !== 'all' ||
    statusFilter !== 'all' ||
    mandatoryFilter !== 'all' ||
    platforms.length > 0;
  const clearFilters = (): void => {
    setSearch('');
    setChannelFilter('all');
    setStatusFilter('all');
    setMandatoryFilter('all');
    setPlatforms([]);
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        {canManage && (
          <PageActions>
            <Button
              onClick={() => {
                navigate({ to: '/releases/register' }).catch(() => undefined);
              }}
            >
              {copy.registerButton}
            </Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <InlineAlert variant="info">
          {copy.infoIntro}
          <strong>{copy.infoOnlyR2Strong}</strong>
          {copy.infoAfterOnlyR2}
          <strong>{copy.infoPromoteStrong}</strong>
          {copy.infoAfterPromote}
          <strong>{copy.infoMandatoryStrong}</strong>
          {copy.infoAfterMandatory}
        </InlineAlert>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ReleaseTab)}>
          <TabsList>
            <TabsTrigger value="releases">{copy.releasesTab}</TabsTrigger>
            {canManage && <TabsTrigger value="bucket">{copy.bucketTab}</TabsTrigger>}
          </TabsList>
          <TabsContent value="releases" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <SearchInput
                value={search}
                onChange={setSearch}
                ariaLabel={copy.searchAriaLabel}
                placeholder={copy.searchPlaceholder}
              />
              <SegmentedControl
                value={channelFilter}
                onChange={setChannelFilter}
                options={channelOptions}
                ariaLabel={copy.channelFilterAriaLabel}
              />
              <SegmentedControl
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
                ariaLabel={copy.statusFilterAriaLabel}
              />
              <SegmentedControl
                value={mandatoryFilter}
                onChange={setMandatoryFilter}
                options={mandatoryOptions}
                ariaLabel={copy.mandatoryFilterAriaLabel}
              />
              <FacetMultiSelect
                label={copy.platformFacetLabel}
                values={platforms}
                onChange={setPlatforms}
                options={RELEASE_PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
              />
            </div>
            {canManage && (
              <ReleaseBulkBar
                selectedCount={selectedVisible.length}
                promotionBlocked={selectedVisible.some(
                  (release) => release.storageState !== 'present',
                )}
                busy={busy}
                onApply={requestBulk}
                onClear={() => setSelectedIds(new Set())}
              />
            )}
            {isError ? (
              <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
            ) : isLoading ? (
              <TableSkeleton columns={canManage ? 8 : 6} />
            ) : (
              <ReleasesTable
                releases={releases}
                canManage={canManage}
                busy={busy}
                hasFilters={hasFilters}
                selectedIds={selectedIds}
                onClearFilters={clearFilters}
                onToggle={toggle}
                onToggleAll={toggleAll}
                onPromote={onPromote}
                onTakedown={onTakedown}
                onSetMandatory={(channel, version, mandatory) => {
                  updateMandatory.mutate(
                    { channel, version, mandatory },
                    {
                      onSuccess: () =>
                        toast.success(
                          mandatory ? copy.toastMandatoryUpdated : copy.toastOptionalUpdated,
                        ),
                      onError: (err) => toast.error(releaseErrorMessage(err)),
                    },
                  );
                }}
                onDelete={(release) => setPending({ kind: 'delete', release })}
                onPurge={(release) => setPending({ kind: 'purge', release })}
                onEditNotes={(release) => setNotesFor(release)}
              />
            )}
            {hasNextPage && (
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  loading={isFetchingNextPage}
                  onClick={() => {
                    fetchNextPage().catch(() => undefined);
                  }}
                >
                  {copy.loadMore}
                </Button>
              </div>
            )}
          </TabsContent>
          {canManage && (
            <TabsContent value="bucket">
              {/* Lazy: só monta (e dispara useReleaseBucket) com a aba ativa. */}
              {activeTab === 'bucket' && <ReleaseBucketPanel />}
            </TabsContent>
          )}
        </Tabs>
      </PageContent>
      {notesFor !== null && (
        <ReleaseNotesModal open onClose={() => setNotesFor(null)} release={notesFor} />
      )}
      <ReleaseConfirmDialog
        pending={pending}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={runPending}
      />
    </Page>
  );
}
